import { useState, useRef, useMemo } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'

import { ArrowLeft, Plus, Calendar, Settings, MessageSquare, Check, X, Clock, Send, Trash2, Edit, Upload, Video, ChevronLeft, ChevronRight, LayoutGrid, Users, FileText, CheckCircle, XCircle, RotateCw, ExternalLink, MoreVertical, HelpCircle } from 'lucide-react'
import { toast } from 'sonner'
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, eachDayOfInterval, isSameMonth, addMonths, subMonths, isToday, parseISO } from 'date-fns'
import { socialMediaCalendarsApi, socialMediaPostsApi, socialMediaMediaApi, socialMediaAccountsApi, socialMediaPublishingApi } from '@/lib/api'
import { useAuth } from '@/contexts/AuthContext'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

import { SocialMediaPost, SocialMediaAccount, CreateSocialMediaPostRequest, CreateSocialMediaAccountRequest, UpdateSocialMediaAccountRequest, SocialMediaPlatform, SocialMediaPostStatus, PostPublishLog, PublishLogStatus } from '@/types'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Checkbox } from '@/components/ui/checkbox'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { usePermissions } from '@/contexts/PermissionContext'
import { CalendarSharingDialog } from '@/components/social-media/CalendarSharingDialog'

const PLATFORMS: { value: SocialMediaPlatform; label: string; icon: string }[] = [
  { value: 'FACEBOOK', label: 'Facebook', icon: '📘' },
  { value: 'INSTAGRAM', label: 'Instagram', icon: '📷' },
  { value: 'TWITTER', label: 'Twitter/X', icon: '🐦' },
  { value: 'LINKEDIN', label: 'LinkedIn', icon: '💼' },
  { value: 'TIKTOK', label: 'TikTok', icon: '🎵' },
  { value: 'YOUTUBE', label: 'YouTube', icon: '📺' },
  { value: 'PINTEREST', label: 'Pinterest', icon: '📌' },
  { value: 'OTHER', label: 'Other', icon: '🌐' },
]

const STATUS_CONFIG: Record<SocialMediaPostStatus, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline'; icon: React.ReactNode }> = {
  DRAFT: { label: 'Draft', variant: 'secondary', icon: <Edit className="h-3 w-3" /> },
  PENDING_APPROVAL: { label: 'Pending Approval', variant: 'outline', icon: <Clock className="h-3 w-3" /> },
  APPROVED: { label: 'Approved', variant: 'default', icon: <Check className="h-3 w-3" /> },
  REJECTED: { label: 'Rejected', variant: 'destructive', icon: <X className="h-3 w-3" /> },
  SCHEDULED: { label: 'Scheduled', variant: 'outline', icon: <Calendar className="h-3 w-3" /> },
  POSTED: { label: 'Posted', variant: 'default', icon: <Send className="h-3 w-3" /> },
}

const LOG_STATUS_CONFIG: Record<PublishLogStatus, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline'; icon: React.ReactNode }> = {
  PENDING: { label: 'Pending', variant: 'secondary', icon: <Clock className="h-3 w-3" /> },
  SUCCESS: { label: 'Success', variant: 'default', icon: <CheckCircle className="h-3 w-3" /> },
  FAILED: { label: 'Failed', variant: 'destructive', icon: <XCircle className="h-3 w-3" /> },
  RETRYING: { label: 'Retrying', variant: 'outline', icon: <RotateCw className="h-3 w-3" /> },
}

// Credential Help Guide Component
function CredentialHelpGuide({ platform }: { platform: SocialMediaPlatform }) {
  const [isOpen, setIsOpen] = useState(false)

  const helpContent: Record<SocialMediaPlatform, { title: string; steps: string[]; links: { label: string; url: string }[] }> = {
    FACEBOOK: {
      title: 'How to get Facebook credentials',
      steps: [
        '1. Go to Facebook Developer Portal (developers.facebook.com)',
        '2. Create a new app or select an existing one',
        '3. Add "Facebook Login" and "Pages API" products to your app',
        '4. Go to Tools → Graph API Explorer',
        '5. Select your app and request permissions: pages_manage_posts, pages_read_engagement',
        '6. Generate a Page Access Token for your Facebook Page',
        '7. Find your Page ID in your Facebook Page\'s About section or via Graph API',
      ],
      links: [
        { label: 'Facebook Developer Portal', url: 'https://developers.facebook.com/' },
        { label: 'Graph API Explorer', url: 'https://developers.facebook.com/tools/explorer/' },
      ],
    },
    INSTAGRAM: {
      title: 'How to get Instagram credentials',
      steps: [
        '1. You need a Facebook Page connected to your Instagram Business/Creator account',
        '2. Go to Facebook Developer Portal (developers.facebook.com)',
        '3. Create an app with Instagram Graph API product',
        '4. Request permissions: instagram_basic, instagram_content_publish',
        '5. Generate an Access Token via Graph API Explorer',
        '6. Get your Instagram Business Account ID using the /me/accounts endpoint',
      ],
      links: [
        { label: 'Instagram Graph API Docs', url: 'https://developers.facebook.com/docs/instagram-api/' },
        { label: 'Getting Started Guide', url: 'https://developers.facebook.com/docs/instagram-api/getting-started' },
      ],
    },
    LINKEDIN: {
      title: 'How to get LinkedIn credentials',
      steps: [
        '1. Go to LinkedIn Developer Portal (developer.linkedin.com)',
        '2. Create a new app or select an existing one',
        '3. Request access to "Share on LinkedIn" and "Sign In with LinkedIn" products',
        '4. Add OAuth 2.0 scopes: w_member_social (for personal) or w_organization_social (for company)',
        '5. Generate an Access Token using OAuth 2.0 flow',
        '6. Your Author URN format: urn:li:person:YOUR_ID or urn:li:organization:COMPANY_ID',
      ],
      links: [
        { label: 'LinkedIn Developer Portal', url: 'https://developer.linkedin.com/' },
        { label: 'Share API Documentation', url: 'https://learn.microsoft.com/en-us/linkedin/marketing/integrations/community-management/shares/share-api' },
      ],
    },
    TWITTER: {
      title: 'How to get Twitter/X credentials',
      steps: [
        '1. Go to Twitter Developer Portal (developer.twitter.com)',
        '2. Create a project and app (requires approved developer account)',
        '3. In your app settings, go to "Keys and Tokens"',
        '4. Generate API Key and API Secret (Consumer Keys)',
        '5. Generate Access Token and Access Token Secret',
        '6. Ensure your app has Read and Write permissions',
      ],
      links: [
        { label: 'Twitter Developer Portal', url: 'https://developer.twitter.com/' },
        { label: 'Authentication Docs', url: 'https://developer.twitter.com/en/docs/authentication/oauth-1-0a' },
      ],
    },
    TIKTOK: {
      title: 'How to get TikTok credentials',
      steps: [
        '1. Go to TikTok Developer Portal (developers.tiktok.com)',
        '2. Create a new app for Content Posting API',
        '3. Submit your app for review (required for posting)',
        '4. Once approved, implement OAuth 2.0 flow to get Access Token',
        '5. The Open ID is returned during the OAuth authorization process',
        '6. Request scopes: video.upload, video.publish',
      ],
      links: [
        { label: 'TikTok Developer Portal', url: 'https://developers.tiktok.com/' },
        { label: 'Content Posting API', url: 'https://developers.tiktok.com/doc/content-posting-api-get-started' },
      ],
    },
    YOUTUBE: {
      title: 'How to get YouTube credentials',
      steps: [
        '1. Go to Google Cloud Console (console.cloud.google.com)',
        '2. Create a project and enable YouTube Data API v3',
        '3. Create OAuth 2.0 credentials (Web application type)',
        '4. Add authorized redirect URIs for your application',
        '5. Implement OAuth 2.0 flow to get Access Token and Refresh Token',
        '6. Find your Channel ID in YouTube Studio → Settings → Channel → Advanced settings',
      ],
      links: [
        { label: 'Google Cloud Console', url: 'https://console.cloud.google.com/' },
        { label: 'YouTube API Docs', url: 'https://developers.google.com/youtube/v3/getting-started' },
      ],
    },
    PINTEREST: {
      title: 'How to get Pinterest credentials',
      steps: [
        '1. Go to Pinterest Developer Portal (developers.pinterest.com)',
        '2. Create a new app',
        '3. Request access to pins:read, pins:write, boards:read scopes',
        '4. Implement OAuth 2.0 flow to get Access Token',
        '5. Get your Board ID from the board URL or via API',
        '6. Board ID format: username/board-name or numeric ID',
      ],
      links: [
        { label: 'Pinterest Developer Portal', url: 'https://developers.pinterest.com/' },
        { label: 'API Documentation', url: 'https://developers.pinterest.com/docs/api/v5/' },
      ],
    },
    OTHER: {
      title: 'Generic API credentials',
      steps: [
        '1. Refer to your platform\'s developer documentation',
        '2. Create an application/project in the developer portal',
        '3. Generate API credentials (usually API key or OAuth tokens)',
        '4. Ensure you have the necessary permissions for posting content',
      ],
      links: [],
    },
  }

  const content = helpContent[platform]

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} className="mt-2">
      <CollapsibleTrigger asChild>
        <Button variant="ghost" size="sm" className="gap-2 text-muted-foreground hover:text-foreground p-0 h-auto">
          <HelpCircle className="h-4 w-4" />
          <span className="text-sm">{isOpen ? 'Hide help guide' : 'How to get these credentials?'}</span>
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent className="mt-3">
        <div className="p-3 bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg space-y-3">
          <h4 className="font-medium text-sm text-blue-900 dark:text-blue-100">{content.title}</h4>
          <ol className="text-xs text-blue-800 dark:text-blue-200 space-y-1">
            {content.steps.map((step, index) => (
              <li key={index}>{step}</li>
            ))}
          </ol>
          {content.links.length > 0 && (
            <div className="flex flex-wrap gap-2 pt-2">
              {content.links.map((link, index) => (
                <a
                  key={index}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400 hover:underline"
                >
                  <ExternalLink className="h-3 w-3" />
                  {link.label}
                </a>
              ))}
            </div>
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  )
}

export default function SocialMediaCalendarDetailPage() {
  const { id } = useParams<{ id: string }>()
  const queryClient = useQueryClient()
  const { canAddEdit } = usePermissions()
  const { user } = useAuth()
  const [activeTab, setActiveTab] = useState('posts')
  const [createPostOpen, setCreatePostOpen] = useState(false)
  const [createAccountOpen, setCreateAccountOpen] = useState(false)
  const [postFormData, setPostFormData] = useState<CreateSocialMediaPostRequest>({ post_content: '', media_urls: [], post_title: '' })
  const [accountFormData, setAccountFormData] = useState<CreateSocialMediaAccountRequest>({
    platform: 'FACEBOOK' as SocialMediaPlatform,
    account_name: '',
    account_handle: '',
    auto_posting_enabled: false,
    credentials: undefined
  })
  const [mediaFiles, setMediaFiles] = useState<{ file: File; preview: string; type: 'image' | 'video' }[]>([])
  const [isUploading, setIsUploading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [selectedPost, setSelectedPost] = useState<SocialMediaPost | null>(null)
  const [postDetailOpen, setPostDetailOpen] = useState(false)
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false)
  const [rejectReason, setRejectReason] = useState('')
  const [editScheduledAt, setEditScheduledAt] = useState<string>('')
  const [sharingDialogOpen, setSharingDialogOpen] = useState(false)
  const [editAccountOpen, setEditAccountOpen] = useState(false)
  const [accountToEdit, setAccountToEdit] = useState<SocialMediaAccount | null>(null)
  const [editAccountFormData, setEditAccountFormData] = useState<UpdateSocialMediaAccountRequest>({})
  const [deleteAccountDialogOpen, setDeleteAccountDialogOpen] = useState(false)
  const [accountToDelete, setAccountToDelete] = useState<SocialMediaAccount | null>(null)

  const { data: calendar, isLoading: calendarLoading } = useQuery({
    queryKey: ['socialMediaCalendar', id],
    queryFn: () => socialMediaCalendarsApi.getById(id!),
    enabled: !!id,
  })

  const { data: accounts = [] } = useQuery({
    queryKey: ['socialMediaAccounts', id],
    queryFn: () => socialMediaCalendarsApi.getAccounts(id!),
    enabled: !!id,
  })

  const { data: posts = [] } = useQuery({
    queryKey: ['socialMediaPosts', id],
    queryFn: () => socialMediaCalendarsApi.getPosts(id!, { active_only: true }),
    enabled: !!id,
  })

  const { data: publishLogs = [], isLoading: logsLoading } = useQuery({
    queryKey: ['publishLogs', id],
    queryFn: () => socialMediaPublishingApi.listPublishLogs({ calendar_id: id }),
    enabled: !!id,
  })

  const { data: calendarMembers = [] } = useQuery({
    queryKey: ['calendarMembers', id],
    queryFn: () => socialMediaCalendarsApi.getMembers(id!),
    enabled: !!id,
  })

  // Check if current user can edit this calendar (has OWNER or EDITOR role)
  const canEditCalendar = useMemo(() => {
    if (!user?.user_id) return false
    const currentUserMember = calendarMembers.find(m => m.user_id === user.user_id)
    if (!currentUserMember) return false
    return currentUserMember.member_role === 'OWNER' || currentUserMember.member_role === 'EDITOR'
  }, [calendarMembers, user?.user_id])

  const createPostMutation = useMutation({
    mutationFn: (data: CreateSocialMediaPostRequest) => socialMediaCalendarsApi.createPost(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['socialMediaPosts', id] })
      queryClient.invalidateQueries({ queryKey: ['socialMediaCalendar', id] })
      toast.success('Post created successfully')
      setCreatePostOpen(false)
      setPostFormData({ post_content: '', media_urls: [], post_title: '' })
      setMediaFiles([])
    },
    onError: (error: any) => {
      const message = error?.response?.data?.message || 'Failed to create post'
      toast.error(message)
    },
  })

  const createAccountMutation = useMutation({
    mutationFn: (data: CreateSocialMediaAccountRequest) =>
      socialMediaCalendarsApi.createAccount(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['socialMediaAccounts', id] })
      queryClient.invalidateQueries({ queryKey: ['socialMediaCalendar', id] })
      toast.success('Account added successfully')
      setCreateAccountOpen(false)
      setAccountFormData({ platform: 'FACEBOOK', account_name: '', account_handle: '', auto_posting_enabled: false, credentials: undefined })
    },
    onError: () => toast.error('Failed to add account'),
  })

  const updateAccountMutation = useMutation({
    mutationFn: ({ accountId, data }: { accountId: string; data: UpdateSocialMediaAccountRequest }) =>
      socialMediaAccountsApi.update(accountId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['socialMediaAccounts', id] })
      toast.success('Account updated successfully')
      setEditAccountOpen(false)
      setAccountToEdit(null)
      setEditAccountFormData({})
    },
    onError: () => toast.error('Failed to update account'),
  })

  const deleteAccountMutation = useMutation({
    mutationFn: (accountId: string) => socialMediaAccountsApi.delete(accountId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['socialMediaAccounts', id] })
      queryClient.invalidateQueries({ queryKey: ['socialMediaCalendar', id] })
      toast.success('Account deleted successfully')
      setDeleteAccountDialogOpen(false)
      setAccountToDelete(null)
    },
    onError: () => toast.error('Failed to delete account'),
  })

  const requestApprovalMutation = useMutation({
    mutationFn: (postId: string) => socialMediaPostsApi.requestApproval(postId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['socialMediaPosts', id] })
      toast.success('Approval requested')
      setPostDetailOpen(false)
    },
    onError: () => toast.error('Failed to request approval'),
  })

  const approveMutation = useMutation({
    mutationFn: (postId: string) => socialMediaPostsApi.approve(postId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['socialMediaPosts', id] })
      toast.success('Post approved')
      setPostDetailOpen(false)
    },
    onError: (e: any) => toast.error(e.response?.data?.message || 'Failed to approve'),
  })

  const rejectMutation = useMutation({
    mutationFn: ({ postId, reason }: { postId: string; reason: string }) => socialMediaPostsApi.reject(postId, { rejection_reason: reason }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['socialMediaPosts', id] })
      toast.success('Post rejected')
      setRejectDialogOpen(false)
      setPostDetailOpen(false)
      setRejectReason('')
    },
    onError: () => toast.error('Failed to reject'),
  })

  const markPostedMutation = useMutation({
    mutationFn: (postId: string) => socialMediaPostsApi.markAsPosted(postId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['socialMediaPosts', id] })
      toast.success('Marked as posted')
      setPostDetailOpen(false)
    },
    onError: () => toast.error('Failed to mark as posted'),
  })

  const deletePostMutation = useMutation({
    mutationFn: (postId: string) => socialMediaPostsApi.delete(postId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['socialMediaPosts', id] })
      queryClient.invalidateQueries({ queryKey: ['socialMediaCalendar', id] })
      toast.success('Post deleted')
      setPostDetailOpen(false)
    },
    onError: () => toast.error('Failed to delete post'),
  })

  const updateScheduleMutation = useMutation({
    mutationFn: ({ postId, scheduled_at }: { postId: string; scheduled_at: string | null }) =>
      socialMediaPostsApi.updateSchedule(postId, scheduled_at),
    onSuccess: (updatedPost) => {
      queryClient.invalidateQueries({ queryKey: ['socialMediaPosts', id] })
      setSelectedPost(updatedPost)
      toast.success('Schedule updated')
    },
    onError: () => toast.error('Failed to update schedule'),
  })

  if (calendarLoading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>
  }

  if (!calendar) {
    return <div className="text-center py-12"><h2 className="text-xl font-semibold">Calendar not found</h2></div>
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/social-media/calendars"><Button variant="ghost" size="icon"><ArrowLeft className="h-4 w-4" /></Button></Link>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: calendar.calendar_color }}>
              <Calendar className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">{calendar.calendar_name}</h1>
              {calendar.client_name && <p className="text-muted-foreground">{calendar.client_name}</p>}
            </div>
          </div>
        </div>
        {canAddEdit('SOCIAL_MEDIA_CALENDARS') && (
          <Button variant="outline" onClick={() => setSharingDialogOpen(true)}>
            <Users className="mr-2 h-4 w-4" />
            Share
          </Button>
        )}
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="flex items-center justify-between">
          <TabsList>
            <TabsTrigger value="posts"><LayoutGrid className="h-4 w-4 mr-2" />Posts</TabsTrigger>
            <TabsTrigger value="calendar"><Calendar className="h-4 w-4 mr-2" />Calendar</TabsTrigger>
            <TabsTrigger value="accounts"><Settings className="h-4 w-4 mr-2" />Accounts</TabsTrigger>
            <TabsTrigger value="logs"><FileText className="h-4 w-4 mr-2" />Posting Logs</TabsTrigger>
          </TabsList>
          {canAddEdit('SOCIAL_MEDIA_CALENDARS') && canEditCalendar && (
            <div className="flex gap-2">
              {(activeTab === 'posts' || activeTab === 'calendar') && <Button onClick={() => setCreatePostOpen(true)}><Plus className="mr-2 h-4 w-4" /> New Post</Button>}
              {activeTab === 'accounts' && <Button onClick={() => setCreateAccountOpen(true)}><Plus className="mr-2 h-4 w-4" /> Add Account</Button>}
            </div>
          )}
        </div>

        <TabsContent value="posts" className="mt-6">
          {posts.length === 0 ? (
            <Card><CardContent className="flex flex-col items-center justify-center py-12">
              <MessageSquare className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">No posts yet</h3>
              <p className="text-muted-foreground mb-4">Create your first post for this calendar</p>
              {canAddEdit('SOCIAL_MEDIA_CALENDARS') && canEditCalendar && <Button onClick={() => setCreatePostOpen(true)}><Plus className="mr-2 h-4 w-4" /> Create Post</Button>}
            </CardContent></Card>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {posts.map((post) => (
                <PostCard key={post.post_id} post={post} onView={() => { setSelectedPost(post); setEditScheduledAt(''); setPostDetailOpen(true) }} />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="calendar" className="mt-6">
          <PostCalendarView
            posts={posts}
            onPostClick={(post) => { setSelectedPost(post); setEditScheduledAt(''); setPostDetailOpen(true) }}
          />
        </TabsContent>

        <TabsContent value="accounts" className="mt-6">
          {accounts.length === 0 ? (
            <Card><CardContent className="flex flex-col items-center justify-center py-12">
              <Settings className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">No accounts connected</h3>
              <p className="text-muted-foreground mb-4">Add social media accounts to this calendar</p>
              {canAddEdit('SOCIAL_MEDIA_CALENDARS') && canEditCalendar && <Button onClick={() => setCreateAccountOpen(true)}><Plus className="mr-2 h-4 w-4" /> Add Account</Button>}
            </CardContent></Card>
          ) : (
            <Card>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Platform</TableHead>
                      <TableHead>Account Name</TableHead>
                      <TableHead>Handle</TableHead>
                      <TableHead>Auto Posting</TableHead>
                      <TableHead className="w-[80px]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {accounts.map((account) => {
                      const platform = PLATFORMS.find(p => p.value === account.platform)
                      return (
                        <TableRow key={account.account_id}>
                          <TableCell>
                            <span>{platform?.label || account.platform}</span>
                          </TableCell>
                          <TableCell>
                            <span className="font-medium">{account.account_name}</span>
                          </TableCell>
                          <TableCell>
                            <span className="text-muted-foreground">{account.account_handle || '-'}</span>
                          </TableCell>
                          <TableCell>
                            <Badge variant={account.auto_posting_enabled ? 'default' : 'secondary'}>
                              {account.auto_posting_enabled ? 'Yes' : 'No'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {canAddEdit('SOCIAL_MEDIA_CALENDARS') && canEditCalendar && (
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="icon">
                                    <MoreVertical className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem onClick={() => {
                                    setAccountToEdit(account)
                                    setEditAccountFormData({
                                      platform: account.platform,
                                      account_name: account.account_name,
                                      account_handle: account.account_handle || '',
                                      account_url: account.account_url || '',
                                      is_active: account.is_active,
                                      auto_posting_enabled: account.auto_posting_enabled,
                                    })
                                    setEditAccountOpen(true)
                                  }}>
                                    <Edit className="mr-2 h-4 w-4" />
                                    Edit
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem
                                    className="text-destructive"
                                    onClick={() => {
                                      setAccountToDelete(account)
                                      setDeleteAccountDialogOpen(true)
                                    }}
                                  >
                                    <Trash2 className="mr-2 h-4 w-4" />
                                    Delete
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            )}
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="logs" className="mt-6">
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Account</TableHead>
                    <TableHead>Platform</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Platform Post</TableHead>
                    <TableHead>Error</TableHead>
                    <TableHead>Retries</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logsLoading ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8">
                        <div className="flex items-center justify-center">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : publishLogs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                        No posting logs found
                      </TableCell>
                    </TableRow>
                  ) : (
                    publishLogs.map((log: PostPublishLog) => {
                      const statusConfig = LOG_STATUS_CONFIG[log.status]
                      return (
                        <TableRow key={log.log_id}>
                          <TableCell className="whitespace-nowrap">
                            {format(new Date(log.created_at), 'MMM d, yyyy h:mm a')}
                          </TableCell>
                          <TableCell>{log.account_name || '-'}</TableCell>
                          <TableCell>{log.platform || '-'}</TableCell>
                          <TableCell>
                            <Badge variant={statusConfig.variant} className="flex items-center gap-1 w-fit">
                              {statusConfig.icon} {statusConfig.label}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {log.platform_post_url ? (
                              <a
                                href={log.platform_post_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-1 text-primary hover:underline"
                              >
                                View <ExternalLink className="h-3 w-3" />
                              </a>
                            ) : log.platform_post_id ? (
                              <span className="text-xs text-muted-foreground">{log.platform_post_id}</span>
                            ) : (
                              '-'
                            )}
                          </TableCell>
                          <TableCell>
                            {log.error_message ? (
                              <span className="text-xs text-destructive" title={log.error_message}>
                                {log.error_message.length > 50 ? `${log.error_message.substring(0, 50)}...` : log.error_message}
                              </span>
                            ) : '-'}
                          </TableCell>
                          <TableCell>{log.retry_count}</TableCell>
                        </TableRow>
                      )
                    })
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create Post Dialog */}
      <Dialog open={createPostOpen} onOpenChange={(open) => {
        setCreatePostOpen(open)
        if (!open) {
          setMediaFiles([])
          setPostFormData({ post_content: '', media_urls: [], post_title: '' })
        }
      }}>
        <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Create Post</DialogTitle><DialogDescription>Create a new social media post</DialogDescription></DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label>Caption / Title (Optional)</Label>
              <Input value={postFormData.post_title || ''} onChange={(e) => setPostFormData({ ...postFormData, post_title: e.target.value })} placeholder="Enter a caption or title for your post..." />
            </div>
            <div className="grid gap-2">
              <Label>Content *</Label>
              <Textarea value={postFormData.post_content} onChange={(e) => setPostFormData({ ...postFormData, post_content: e.target.value })} placeholder="Write your post content..." rows={5} />
            </div>
            <div className="grid gap-2">
              <Label>Media (Images/Videos)</Label>
              <div className="border-2 border-dashed rounded-lg p-4">
                <input ref={fileInputRef} type="file" className="hidden" accept="image/*,video/*" multiple
                  onChange={(e) => {
                    const files = Array.from(e.target.files || [])
                    const newMedia = files.map(file => ({
                      file,
                      preview: URL.createObjectURL(file),
                      type: file.type.startsWith('video/') ? 'video' as const : 'image' as const
                    }))
                    setMediaFiles(prev => [...prev, ...newMedia])
                    e.target.value = ''
                  }}
                />
                {mediaFiles.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-4 cursor-pointer" onClick={() => fileInputRef.current?.click()}>
                    <Upload className="h-8 w-8 text-muted-foreground mb-2" />
                    <p className="text-sm text-muted-foreground">Click to upload images or videos</p>
                    <p className="text-xs text-muted-foreground mt-1">Supports JPG, PNG, GIF, MP4, MOV</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="grid grid-cols-3 gap-2">
                      {mediaFiles.map((media, index) => (
                        <div key={index} className="relative group">
                          {media.type === 'image' ? (
                            <img src={media.preview} alt={`Media ${index + 1}`} className="w-full h-24 object-cover rounded-md" />
                          ) : (
                            <div className="w-full h-24 bg-muted rounded-md flex items-center justify-center">
                              <Video className="h-8 w-8 text-muted-foreground" />
                            </div>
                          )}
                          <button type="button" onClick={() => {
                            URL.revokeObjectURL(media.preview)
                            setMediaFiles(prev => prev.filter((_, i) => i !== index))
                          }} className="absolute top-1 right-1 bg-destructive text-destructive-foreground rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                    <Button type="button" variant="outline" size="sm" onClick={() => fileInputRef.current?.click()}>
                      <Plus className="h-4 w-4 mr-1" /> Add More
                    </Button>
                  </div>
                )}
              </div>
              <p className="text-xs text-muted-foreground">Media files are uploaded to cloud storage (S3/GCS) or local storage based on configuration.</p>
            </div>
            <div className="grid gap-2">
              <Label>Target Account (Optional)</Label>
              <Select value={postFormData.account_id || ''} onValueChange={(v) => setPostFormData({ ...postFormData, account_id: v === 'none' ? undefined : v })}>
                <SelectTrigger><SelectValue placeholder="Select account" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No specific account</SelectItem>
                  {accounts.map((a) => (<SelectItem key={a.account_id} value={a.account_id}>{PLATFORMS.find(p => p.value === a.platform)?.icon} {a.account_name}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label>Scheduled Date (Optional)</Label>
              <Input type="datetime-local" value={postFormData.scheduled_at || ''} onChange={(e) => setPostFormData({ ...postFormData, scheduled_at: e.target.value || undefined })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreatePostOpen(false)} disabled={isUploading || createPostMutation.isPending}>Cancel</Button>
            <Button onClick={async () => {
              try {
                setIsUploading(true)
                // Upload media files to S3/storage and get URLs
                const mediaUrls: string[] = []
                for (const media of mediaFiles) {
                  const response = await socialMediaMediaApi.upload(media.file)
                  mediaUrls.push(response.url)
                }
                createPostMutation.mutate({ ...postFormData, media_urls: mediaUrls })
              } catch (error: any) {
                const message = error?.response?.data?.message || 'Failed to upload media files'
                toast.error(message)
              } finally {
                setIsUploading(false)
              }
            }} disabled={isUploading || createPostMutation.isPending || !postFormData.post_content.trim()}>
              {isUploading ? 'Uploading...' : createPostMutation.isPending ? 'Creating...' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Account Dialog */}
      <Dialog open={createAccountOpen} onOpenChange={setCreateAccountOpen}>
        <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Add Account</DialogTitle><DialogDescription>Add a social media account to this calendar</DialogDescription></DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label>Platform *</Label>
              <Select value={accountFormData.platform} onValueChange={(v) => setAccountFormData({ ...accountFormData, platform: v as SocialMediaPlatform, credentials: undefined })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{PLATFORMS.map((p) => (<SelectItem key={p.value} value={p.value}>{p.icon} {p.label}</SelectItem>))}</SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label>Account Name *</Label>
              <Input value={accountFormData.account_name} onChange={(e) => setAccountFormData({ ...accountFormData, account_name: e.target.value })} placeholder="e.g., Company Facebook" />
            </div>
            <div className="grid gap-2">
              <Label>Handle (Optional)</Label>
              <Input value={accountFormData.account_handle || ''} onChange={(e) => setAccountFormData({ ...accountFormData, account_handle: e.target.value })} placeholder="e.g., @company" />
            </div>

            {/* Auto-posting toggle */}
            <div className="flex items-center space-x-2">
              <Checkbox
                id="auto_posting"
                checked={accountFormData.auto_posting_enabled || false}
                onCheckedChange={(checked) => setAccountFormData({
                  ...accountFormData,
                  auto_posting_enabled: checked === true,
                  credentials: checked ? (accountFormData.credentials || {}) : undefined
                })}
              />
              <Label htmlFor="auto_posting" className="text-sm font-normal cursor-pointer">
                Enable auto-posting (requires API credentials)
              </Label>
            </div>

            {/* Credentials section - shown when auto-posting is enabled */}
            {accountFormData.auto_posting_enabled && (
              <div className="space-y-4 p-4 border rounded-lg bg-muted/50">
                <p className="text-sm text-muted-foreground">
                  Enter your API credentials for {PLATFORMS.find(p => p.value === accountFormData.platform)?.label || 'this platform'}.
                  These will be stored securely and used for auto-posting.
                </p>

                <CredentialHelpGuide platform={accountFormData.platform} />

                {/* Facebook credentials */}
                {accountFormData.platform === 'FACEBOOK' && (
                  <>
                    <div className="grid gap-2">
                      <Label>Page Access Token *</Label>
                      <Input
                        type="password"
                        value={accountFormData.credentials?.page_access_token || ''}
                        onChange={(e) => setAccountFormData({
                          ...accountFormData,
                          credentials: { ...accountFormData.credentials, page_access_token: e.target.value }
                        })}
                        placeholder="Enter your Facebook Page Access Token"
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label>Page ID *</Label>
                      <Input
                        value={accountFormData.credentials?.page_id || ''}
                        onChange={(e) => setAccountFormData({
                          ...accountFormData,
                          credentials: { ...accountFormData.credentials, page_id: e.target.value }
                        })}
                        placeholder="Enter your Facebook Page ID"
                      />
                    </div>
                  </>
                )}

                {/* Instagram credentials */}
                {accountFormData.platform === 'INSTAGRAM' && (
                  <>
                    <div className="grid gap-2">
                      <Label>Access Token *</Label>
                      <Input
                        type="password"
                        value={accountFormData.credentials?.access_token || ''}
                        onChange={(e) => setAccountFormData({
                          ...accountFormData,
                          credentials: { ...accountFormData.credentials, access_token: e.target.value }
                        })}
                        placeholder="Enter your Instagram Access Token"
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label>Instagram Business Account ID *</Label>
                      <Input
                        value={accountFormData.credentials?.instagram_user_id || ''}
                        onChange={(e) => setAccountFormData({
                          ...accountFormData,
                          credentials: { ...accountFormData.credentials, instagram_user_id: e.target.value }
                        })}
                        placeholder="Enter your Instagram Business Account ID"
                      />
                    </div>
                  </>
                )}

                {/* LinkedIn credentials */}
                {accountFormData.platform === 'LINKEDIN' && (
                  <>
                    <div className="grid gap-2">
                      <Label>Access Token *</Label>
                      <Input
                        type="password"
                        value={accountFormData.credentials?.access_token || ''}
                        onChange={(e) => setAccountFormData({
                          ...accountFormData,
                          credentials: { ...accountFormData.credentials, access_token: e.target.value }
                        })}
                        placeholder="Enter your LinkedIn Access Token"
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label>Author URN *</Label>
                      <Input
                        value={accountFormData.credentials?.author_urn || ''}
                        onChange={(e) => setAccountFormData({
                          ...accountFormData,
                          credentials: { ...accountFormData.credentials, author_urn: e.target.value }
                        })}
                        placeholder="e.g., urn:li:person:ABC123 or urn:li:organization:123456"
                      />
                    </div>
                  </>
                )}

                {/* TikTok credentials */}
                {accountFormData.platform === 'TIKTOK' && (
                  <>
                    <div className="grid gap-2">
                      <Label>Access Token *</Label>
                      <Input
                        type="password"
                        value={accountFormData.credentials?.access_token || ''}
                        onChange={(e) => setAccountFormData({
                          ...accountFormData,
                          credentials: { ...accountFormData.credentials, access_token: e.target.value }
                        })}
                        placeholder="Enter your TikTok Access Token"
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label>Open ID *</Label>
                      <Input
                        value={accountFormData.credentials?.open_id || ''}
                        onChange={(e) => setAccountFormData({
                          ...accountFormData,
                          credentials: { ...accountFormData.credentials, open_id: e.target.value }
                        })}
                        placeholder="Enter your TikTok Open ID"
                      />
                    </div>
                  </>
                )}

                {/* Twitter/X credentials */}
                {accountFormData.platform === 'TWITTER' && (
                  <>
                    <div className="grid gap-2">
                      <Label>API Key *</Label>
                      <Input
                        type="password"
                        value={accountFormData.credentials?.api_key || ''}
                        onChange={(e) => setAccountFormData({
                          ...accountFormData,
                          credentials: { ...accountFormData.credentials, api_key: e.target.value }
                        })}
                        placeholder="Enter your Twitter API Key"
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label>API Secret *</Label>
                      <Input
                        type="password"
                        value={accountFormData.credentials?.api_secret || ''}
                        onChange={(e) => setAccountFormData({
                          ...accountFormData,
                          credentials: { ...accountFormData.credentials, api_secret: e.target.value }
                        })}
                        placeholder="Enter your Twitter API Secret"
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label>Access Token *</Label>
                      <Input
                        type="password"
                        value={accountFormData.credentials?.access_token || ''}
                        onChange={(e) => setAccountFormData({
                          ...accountFormData,
                          credentials: { ...accountFormData.credentials, access_token: e.target.value }
                        })}
                        placeholder="Enter your Twitter Access Token"
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label>Access Token Secret *</Label>
                      <Input
                        type="password"
                        value={accountFormData.credentials?.access_token_secret || ''}
                        onChange={(e) => setAccountFormData({
                          ...accountFormData,
                          credentials: { ...accountFormData.credentials, access_token_secret: e.target.value }
                        })}
                        placeholder="Enter your Twitter Access Token Secret"
                      />
                    </div>
                  </>
                )}

                {/* YouTube credentials */}
                {accountFormData.platform === 'YOUTUBE' && (
                  <>
                    <div className="grid gap-2">
                      <Label>Access Token *</Label>
                      <Input
                        type="password"
                        value={accountFormData.credentials?.access_token || ''}
                        onChange={(e) => setAccountFormData({
                          ...accountFormData,
                          credentials: { ...accountFormData.credentials, access_token: e.target.value }
                        })}
                        placeholder="Enter your YouTube Access Token"
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label>Refresh Token</Label>
                      <Input
                        type="password"
                        value={accountFormData.credentials?.refresh_token || ''}
                        onChange={(e) => setAccountFormData({
                          ...accountFormData,
                          credentials: { ...accountFormData.credentials, refresh_token: e.target.value }
                        })}
                        placeholder="Enter your YouTube Refresh Token"
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label>Channel ID *</Label>
                      <Input
                        value={accountFormData.credentials?.channel_id || ''}
                        onChange={(e) => setAccountFormData({
                          ...accountFormData,
                          credentials: { ...accountFormData.credentials, channel_id: e.target.value }
                        })}
                        placeholder="Enter your YouTube Channel ID"
                      />
                    </div>
                  </>
                )}

                {/* Pinterest credentials */}
                {accountFormData.platform === 'PINTEREST' && (
                  <>
                    <div className="grid gap-2">
                      <Label>Access Token *</Label>
                      <Input
                        type="password"
                        value={accountFormData.credentials?.access_token || ''}
                        onChange={(e) => setAccountFormData({
                          ...accountFormData,
                          credentials: { ...accountFormData.credentials, access_token: e.target.value }
                        })}
                        placeholder="Enter your Pinterest Access Token"
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label>Board ID *</Label>
                      <Input
                        value={accountFormData.credentials?.board_id || ''}
                        onChange={(e) => setAccountFormData({
                          ...accountFormData,
                          credentials: { ...accountFormData.credentials, board_id: e.target.value }
                        })}
                        placeholder="Enter your Pinterest Board ID"
                      />
                    </div>
                  </>
                )}

                {/* Other platform - generic access token */}
                {accountFormData.platform === 'OTHER' && (
                  <div className="grid gap-2">
                    <Label>Access Token</Label>
                    <Input
                      type="password"
                      value={accountFormData.credentials?.access_token || ''}
                      onChange={(e) => setAccountFormData({
                        ...accountFormData,
                        credentials: { ...accountFormData.credentials, access_token: e.target.value }
                      })}
                      placeholder="Enter your Access Token"
                    />
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateAccountOpen(false)}>Cancel</Button>
            <Button onClick={() => createAccountMutation.mutate(accountFormData)} disabled={createAccountMutation.isPending || !accountFormData.account_name.trim()}>
              {createAccountMutation.isPending ? 'Adding...' : 'Add'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Post Detail Dialog */}
      <Dialog open={postDetailOpen} onOpenChange={setPostDetailOpen}>
        <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">Post Details {selectedPost && <Badge variant={STATUS_CONFIG[selectedPost.status].variant}>{STATUS_CONFIG[selectedPost.status].icon} {STATUS_CONFIG[selectedPost.status].label}</Badge>}</DialogTitle>
          </DialogHeader>
          {selectedPost && (
            <div className="space-y-4">
              {/* Media Gallery */}
              {selectedPost.media_urls && selectedPost.media_urls.length > 0 && (
                <div className="grid grid-cols-2 gap-2">
                  {selectedPost.media_urls.map((url, index) => (
                    <div key={index} className="relative rounded-lg overflow-hidden bg-muted">
                      {url.startsWith('data:video') ? (
                        <video src={url} controls className="w-full h-40 object-cover" />
                      ) : (
                        <img src={url} alt={`Media ${index + 1}`} className="w-full h-40 object-cover cursor-pointer" onClick={() => window.open(url, '_blank')} />
                      )}
                    </div>
                  ))}
                </div>
              )}
              {/* Account & Scheduled Date Info */}
              <div className="grid grid-cols-2 gap-4 p-3 bg-muted/50 rounded-lg">
                <div>
                  <Label className="text-xs text-muted-foreground">Account</Label>
                  <div className="flex items-center gap-2 mt-1">
                    {selectedPost.account_platform && (
                      <span>{PLATFORMS.find(p => p.value === selectedPost.account_platform)?.icon || '🌐'}</span>
                    )}
                    <span className="font-medium">{selectedPost.account_name || 'No account assigned'}</span>
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Scheduled Date</Label>
                  <p className="font-medium mt-1">
                    {selectedPost.scheduled_at ? format(new Date(selectedPost.scheduled_at), 'PPp') : 'Not scheduled'}
                  </p>
                </div>
              </div>

              {/* Caption */}
              {selectedPost.post_title && (
                <div>
                  <Label className="text-xs text-muted-foreground">Caption</Label>
                  <p className="font-medium">{selectedPost.post_title}</p>
                </div>
              )}
              {/* Content */}
              <div className="p-4 bg-muted rounded-lg"><p className="whitespace-pre-wrap">{selectedPost.post_content}</p></div>

              {/* Schedule Date Editor - available for non-POSTED statuses */}
              {selectedPost.status !== 'POSTED' && canAddEdit('SOCIAL_MEDIA_CALENDARS') && canEditCalendar && (
                <div className="p-3 border rounded-lg space-y-2">
                  <Label className="text-sm font-medium">Set/Update Schedule Date</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="datetime-local"
                      value={editScheduledAt || (selectedPost.scheduled_at ? format(new Date(selectedPost.scheduled_at), "yyyy-MM-dd'T'HH:mm") : '')}
                      onChange={(e) => setEditScheduledAt(e.target.value)}
                      className="flex-1"
                    />
                    <Button
                      size="sm"
                      onClick={() => {
                        updateScheduleMutation.mutate({
                          postId: selectedPost.post_id,
                          scheduled_at: editScheduledAt || null
                        })
                        setEditScheduledAt('')
                      }}
                      disabled={updateScheduleMutation.isPending || !editScheduledAt}
                    >
                      <Calendar className="mr-2 h-4 w-4" />
                      {updateScheduleMutation.isPending ? 'Saving...' : 'Update'}
                    </Button>
                    {selectedPost.scheduled_at && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          updateScheduleMutation.mutate({
                            postId: selectedPost.post_id,
                            scheduled_at: null
                          })
                          setEditScheduledAt('')
                        }}
                        disabled={updateScheduleMutation.isPending}
                      >
                        Clear
                      </Button>
                    )}
                  </div>
                </div>
              )}

              {selectedPost.rejection_reason && <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg"><p className="text-sm text-destructive"><strong>Rejection reason:</strong> {selectedPost.rejection_reason}</p></div>}
              <div className="flex flex-wrap gap-2">
                {selectedPost.status === 'DRAFT' && canAddEdit('SOCIAL_MEDIA_CALENDARS') && canEditCalendar && (
                  <Button onClick={() => requestApprovalMutation.mutate(selectedPost.post_id)} disabled={requestApprovalMutation.isPending}><Send className="mr-2 h-4 w-4" /> Request Approval</Button>
                )}
                {selectedPost.status === 'PENDING_APPROVAL' && canAddEdit('SOCIAL_MEDIA_CALENDARS') && canEditCalendar && (
                  <>
                    <Button onClick={() => approveMutation.mutate(selectedPost.post_id)} disabled={approveMutation.isPending}><Check className="mr-2 h-4 w-4" /> Approve</Button>
                    <Button variant="destructive" onClick={() => setRejectDialogOpen(true)}><X className="mr-2 h-4 w-4" /> Reject</Button>
                  </>
                )}
                {(selectedPost.status === 'APPROVED' || selectedPost.status === 'SCHEDULED') && canAddEdit('SOCIAL_MEDIA_CALENDARS') && canEditCalendar && (
                  <Button onClick={() => markPostedMutation.mutate(selectedPost.post_id)} disabled={markPostedMutation.isPending}><Check className="mr-2 h-4 w-4" /> Mark as Posted</Button>
                )}
                {canAddEdit('SOCIAL_MEDIA_CALENDARS') && canEditCalendar && selectedPost.status !== 'POSTED' && (
                  <Button variant="outline" className="text-destructive" onClick={() => deletePostMutation.mutate(selectedPost.post_id)} disabled={deletePostMutation.isPending}><Trash2 className="mr-2 h-4 w-4" /> Delete</Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Reject Post</DialogTitle><DialogDescription>Provide a reason for rejection</DialogDescription></DialogHeader>
          <div className="py-4"><Textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="Reason for rejection..." rows={3} /></div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDialogOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={() => selectedPost && rejectMutation.mutate({ postId: selectedPost.post_id, reason: rejectReason })} disabled={rejectMutation.isPending || !rejectReason.trim()}>
              {rejectMutation.isPending ? 'Rejecting...' : 'Reject'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Account Dialog */}
      <Dialog open={editAccountOpen} onOpenChange={(open) => {
        setEditAccountOpen(open)
        if (!open) {
          setAccountToEdit(null)
          setEditAccountFormData({})
        }
      }}>
        <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Account</DialogTitle>
            <DialogDescription>Update the social media account details</DialogDescription>
          </DialogHeader>
          {accountToEdit && (
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label>Platform</Label>
                <Select
                  value={editAccountFormData.platform || accountToEdit.platform}
                  onValueChange={(v) => setEditAccountFormData({ ...editAccountFormData, platform: v as SocialMediaPlatform })}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PLATFORMS.map((p) => (
                      <SelectItem key={p.value} value={p.value}>{p.icon} {p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label>Account Name *</Label>
                <Input
                  value={editAccountFormData.account_name ?? accountToEdit.account_name}
                  onChange={(e) => setEditAccountFormData({ ...editAccountFormData, account_name: e.target.value })}
                  placeholder="e.g., Company Facebook"
                />
              </div>
              <div className="grid gap-2">
                <Label>Handle (Optional)</Label>
                <Input
                  value={editAccountFormData.account_handle ?? accountToEdit.account_handle ?? ''}
                  onChange={(e) => setEditAccountFormData({ ...editAccountFormData, account_handle: e.target.value })}
                  placeholder="e.g., @company"
                />
              </div>
              <div className="grid gap-2">
                <Label>Account URL (Optional)</Label>
                <Input
                  value={editAccountFormData.account_url ?? accountToEdit.account_url ?? ''}
                  onChange={(e) => setEditAccountFormData({ ...editAccountFormData, account_url: e.target.value })}
                  placeholder="e.g., https://facebook.com/company"
                />
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="edit-is-active"
                  checked={editAccountFormData.is_active ?? accountToEdit.is_active}
                  onCheckedChange={(checked) => setEditAccountFormData({ ...editAccountFormData, is_active: !!checked })}
                />
                <Label htmlFor="edit-is-active">Active</Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="edit-auto-posting"
                  checked={editAccountFormData.auto_posting_enabled ?? accountToEdit.auto_posting_enabled}
                  onCheckedChange={(checked) => setEditAccountFormData({ ...editAccountFormData, auto_posting_enabled: !!checked })}
                />
                <Label htmlFor="edit-auto-posting">Enable Auto-Posting</Label>
              </div>

              {/* Credentials section - shown when auto-posting is enabled */}
              {(editAccountFormData.auto_posting_enabled ?? accountToEdit.auto_posting_enabled) && (
                <div className="space-y-4 p-4 border rounded-lg bg-muted/50">
                  <p className="text-sm text-muted-foreground">
                    Enter your API credentials for {PLATFORMS.find(p => p.value === (editAccountFormData.platform || accountToEdit.platform))?.label || 'this platform'}.
                    These will be stored securely and used for auto-posting.
                  </p>

                  <div className="p-2 bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 rounded text-xs text-amber-800 dark:text-amber-200">
                    <strong>Note:</strong> For security, existing credentials are not displayed. Leave fields blank to keep current credentials, or enter new values to update them.
                  </div>

                  <CredentialHelpGuide platform={(editAccountFormData.platform || accountToEdit.platform) as SocialMediaPlatform} />

                  {/* Facebook credentials */}
                  {(editAccountFormData.platform || accountToEdit.platform) === 'FACEBOOK' && (
                    <>
                      <div className="grid gap-2">
                        <Label>Page Access Token *</Label>
                        <Input
                          type="password"
                          value={editAccountFormData.credentials?.page_access_token || ''}
                          onChange={(e) => setEditAccountFormData({
                            ...editAccountFormData,
                            credentials: { ...editAccountFormData.credentials, page_access_token: e.target.value }
                          })}
                          placeholder="Enter your Facebook Page Access Token"
                        />
                      </div>
                      <div className="grid gap-2">
                        <Label>Page ID *</Label>
                        <Input
                          value={editAccountFormData.credentials?.page_id || ''}
                          onChange={(e) => setEditAccountFormData({
                            ...editAccountFormData,
                            credentials: { ...editAccountFormData.credentials, page_id: e.target.value }
                          })}
                          placeholder="Enter your Facebook Page ID"
                        />
                      </div>
                    </>
                  )}

                  {/* Instagram credentials */}
                  {(editAccountFormData.platform || accountToEdit.platform) === 'INSTAGRAM' && (
                    <>
                      <div className="grid gap-2">
                        <Label>Access Token *</Label>
                        <Input
                          type="password"
                          value={editAccountFormData.credentials?.access_token || ''}
                          onChange={(e) => setEditAccountFormData({
                            ...editAccountFormData,
                            credentials: { ...editAccountFormData.credentials, access_token: e.target.value }
                          })}
                          placeholder="Enter your Instagram Access Token"
                        />
                      </div>
                      <div className="grid gap-2">
                        <Label>Instagram Business Account ID *</Label>
                        <Input
                          value={editAccountFormData.credentials?.instagram_user_id || ''}
                          onChange={(e) => setEditAccountFormData({
                            ...editAccountFormData,
                            credentials: { ...editAccountFormData.credentials, instagram_user_id: e.target.value }
                          })}
                          placeholder="Enter your Instagram Business Account ID"
                        />
                      </div>
                    </>
                  )}

                  {/* LinkedIn credentials */}
                  {(editAccountFormData.platform || accountToEdit.platform) === 'LINKEDIN' && (
                    <>
                      <div className="grid gap-2">
                        <Label>Access Token *</Label>
                        <Input
                          type="password"
                          value={editAccountFormData.credentials?.access_token || ''}
                          onChange={(e) => setEditAccountFormData({
                            ...editAccountFormData,
                            credentials: { ...editAccountFormData.credentials, access_token: e.target.value }
                          })}
                          placeholder="Enter your LinkedIn Access Token"
                        />
                      </div>
                      <div className="grid gap-2">
                        <Label>Author URN *</Label>
                        <Input
                          value={editAccountFormData.credentials?.author_urn || ''}
                          onChange={(e) => setEditAccountFormData({
                            ...editAccountFormData,
                            credentials: { ...editAccountFormData.credentials, author_urn: e.target.value }
                          })}
                          placeholder="e.g., urn:li:person:ABC123 or urn:li:organization:123456"
                        />
                      </div>
                    </>
                  )}

                  {/* TikTok credentials */}
                  {(editAccountFormData.platform || accountToEdit.platform) === 'TIKTOK' && (
                    <>
                      <div className="grid gap-2">
                        <Label>Access Token *</Label>
                        <Input
                          type="password"
                          value={editAccountFormData.credentials?.access_token || ''}
                          onChange={(e) => setEditAccountFormData({
                            ...editAccountFormData,
                            credentials: { ...editAccountFormData.credentials, access_token: e.target.value }
                          })}
                          placeholder="Enter your TikTok Access Token"
                        />
                      </div>
                      <div className="grid gap-2">
                        <Label>Open ID *</Label>
                        <Input
                          value={editAccountFormData.credentials?.open_id || ''}
                          onChange={(e) => setEditAccountFormData({
                            ...editAccountFormData,
                            credentials: { ...editAccountFormData.credentials, open_id: e.target.value }
                          })}
                          placeholder="Enter your TikTok Open ID"
                        />
                      </div>
                    </>
                  )}

                  {/* Twitter/X credentials */}
                  {(editAccountFormData.platform || accountToEdit.platform) === 'TWITTER' && (
                    <>
                      <div className="grid gap-2">
                        <Label>API Key *</Label>
                        <Input
                          type="password"
                          value={editAccountFormData.credentials?.api_key || ''}
                          onChange={(e) => setEditAccountFormData({
                            ...editAccountFormData,
                            credentials: { ...editAccountFormData.credentials, api_key: e.target.value }
                          })}
                          placeholder="Enter your Twitter API Key"
                        />
                      </div>
                      <div className="grid gap-2">
                        <Label>API Secret *</Label>
                        <Input
                          type="password"
                          value={editAccountFormData.credentials?.api_secret || ''}
                          onChange={(e) => setEditAccountFormData({
                            ...editAccountFormData,
                            credentials: { ...editAccountFormData.credentials, api_secret: e.target.value }
                          })}
                          placeholder="Enter your Twitter API Secret"
                        />
                      </div>
                      <div className="grid gap-2">
                        <Label>Access Token *</Label>
                        <Input
                          type="password"
                          value={editAccountFormData.credentials?.access_token || ''}
                          onChange={(e) => setEditAccountFormData({
                            ...editAccountFormData,
                            credentials: { ...editAccountFormData.credentials, access_token: e.target.value }
                          })}
                          placeholder="Enter your Twitter Access Token"
                        />
                      </div>
                      <div className="grid gap-2">
                        <Label>Access Token Secret *</Label>
                        <Input
                          type="password"
                          value={editAccountFormData.credentials?.access_token_secret || ''}
                          onChange={(e) => setEditAccountFormData({
                            ...editAccountFormData,
                            credentials: { ...editAccountFormData.credentials, access_token_secret: e.target.value }
                          })}
                          placeholder="Enter your Twitter Access Token Secret"
                        />
                      </div>
                    </>
                  )}

                  {/* YouTube credentials */}
                  {(editAccountFormData.platform || accountToEdit.platform) === 'YOUTUBE' && (
                    <>
                      <div className="grid gap-2">
                        <Label>Access Token *</Label>
                        <Input
                          type="password"
                          value={editAccountFormData.credentials?.access_token || ''}
                          onChange={(e) => setEditAccountFormData({
                            ...editAccountFormData,
                            credentials: { ...editAccountFormData.credentials, access_token: e.target.value }
                          })}
                          placeholder="Enter your YouTube Access Token"
                        />
                      </div>
                      <div className="grid gap-2">
                        <Label>Refresh Token</Label>
                        <Input
                          type="password"
                          value={editAccountFormData.credentials?.refresh_token || ''}
                          onChange={(e) => setEditAccountFormData({
                            ...editAccountFormData,
                            credentials: { ...editAccountFormData.credentials, refresh_token: e.target.value }
                          })}
                          placeholder="Enter your YouTube Refresh Token"
                        />
                      </div>
                      <div className="grid gap-2">
                        <Label>Channel ID *</Label>
                        <Input
                          value={editAccountFormData.credentials?.channel_id || ''}
                          onChange={(e) => setEditAccountFormData({
                            ...editAccountFormData,
                            credentials: { ...editAccountFormData.credentials, channel_id: e.target.value }
                          })}
                          placeholder="Enter your YouTube Channel ID"
                        />
                      </div>
                    </>
                  )}

                  {/* Pinterest credentials */}
                  {(editAccountFormData.platform || accountToEdit.platform) === 'PINTEREST' && (
                    <>
                      <div className="grid gap-2">
                        <Label>Access Token *</Label>
                        <Input
                          type="password"
                          value={editAccountFormData.credentials?.access_token || ''}
                          onChange={(e) => setEditAccountFormData({
                            ...editAccountFormData,
                            credentials: { ...editAccountFormData.credentials, access_token: e.target.value }
                          })}
                          placeholder="Enter your Pinterest Access Token"
                        />
                      </div>
                      <div className="grid gap-2">
                        <Label>Board ID *</Label>
                        <Input
                          value={editAccountFormData.credentials?.board_id || ''}
                          onChange={(e) => setEditAccountFormData({
                            ...editAccountFormData,
                            credentials: { ...editAccountFormData.credentials, board_id: e.target.value }
                          })}
                          placeholder="Enter your Pinterest Board ID"
                        />
                      </div>
                    </>
                  )}

                  {/* Other platform - generic access token */}
                  {(editAccountFormData.platform || accountToEdit.platform) === 'OTHER' && (
                    <div className="grid gap-2">
                      <Label>Access Token</Label>
                      <Input
                        type="password"
                        value={editAccountFormData.credentials?.access_token || ''}
                        onChange={(e) => setEditAccountFormData({
                          ...editAccountFormData,
                          credentials: { ...editAccountFormData.credentials, access_token: e.target.value }
                        })}
                        placeholder="Enter your Access Token"
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditAccountOpen(false)}>Cancel</Button>
            <Button
              onClick={() => {
                if (accountToEdit) {
                  // Only include credentials if they have actual values (not empty strings)
                  const dataToSend = { ...editAccountFormData }
                  if (dataToSend.credentials) {
                    const hasCredentialValues = Object.values(dataToSend.credentials).some(
                      (val) => val !== undefined && val !== null && val !== ''
                    )
                    if (!hasCredentialValues) {
                      // Remove empty credentials object to preserve existing credentials
                      delete dataToSend.credentials
                    }
                  }
                  updateAccountMutation.mutate({
                    accountId: accountToEdit.account_id,
                    data: dataToSend
                  })
                }
              }}
              disabled={updateAccountMutation.isPending || !editAccountFormData.account_name?.trim()}
            >
              {updateAccountMutation.isPending ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Account Confirmation Dialog */}
      <Dialog open={deleteAccountDialogOpen} onOpenChange={setDeleteAccountDialogOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Delete Account</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete the account "{accountToDelete?.account_name}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteAccountDialogOpen(false)}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={() => {
                if (accountToDelete) {
                  deleteAccountMutation.mutate(accountToDelete.account_id)
                }
              }}
              disabled={deleteAccountMutation.isPending}
            >
              {deleteAccountMutation.isPending ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Calendar Sharing Dialog */}
      <CalendarSharingDialog
        calendarId={id!}
        calendarName={calendar.calendar_name}
        open={sharingDialogOpen}
        onOpenChange={setSharingDialogOpen}
      />
    </div>
  )
}

function PostCard({ post, onView }: { post: SocialMediaPost; onView: () => void }) {
  const statusConfig = STATUS_CONFIG[post.status]
  const hasMedia = post.media_urls && post.media_urls.length > 0
  return (
    <Card className="hover:shadow-md transition-shadow cursor-pointer overflow-hidden" onClick={onView}>
      {hasMedia && (
        <div className="relative h-32 bg-muted">
          {post.media_urls[0].startsWith('data:video') ? (
            <div className="w-full h-full flex items-center justify-center">
              <Video className="h-8 w-8 text-muted-foreground" />
            </div>
          ) : (
            <img src={post.media_urls[0]} alt="Post media" className="w-full h-full object-cover" />
          )}
          {post.media_urls.length > 1 && (
            <div className="absolute bottom-2 right-2 bg-black/60 text-white text-xs px-2 py-1 rounded">
              +{post.media_urls.length - 1} more
            </div>
          )}
        </div>
      )}
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between">
          <Badge variant={statusConfig.variant} className="flex items-center gap-1">{statusConfig.icon} {statusConfig.label}</Badge>
          {post.account_name && <span className="text-xs text-muted-foreground">{post.account_name}</span>}
        </div>
        {post.post_title && <CardTitle className="text-sm mt-2">{post.post_title}</CardTitle>}
      </CardHeader>
      <CardContent>
        <p className="text-sm line-clamp-3 mb-2">{post.post_content}</p>
        {post.scheduled_at && <p className="text-xs text-muted-foreground"><Clock className="inline h-3 w-3 mr-1" />{format(new Date(post.scheduled_at), 'MMM d, yyyy h:mm a')}</p>}
      </CardContent>
    </Card>
  )
}

// Calendar View Component for Posts
function PostCalendarView({ posts, onPostClick }: { posts: SocialMediaPost[]; onPostClick: (post: SocialMediaPost) => void }) {
  const [currentDate, setCurrentDate] = useState(new Date())

  // Group posts by scheduled date
  const postsByDate = useMemo(() => {
    const grouped: Record<string, SocialMediaPost[]> = {}
    posts.forEach(post => {
      if (post.scheduled_at) {
        const dateKey = format(parseISO(post.scheduled_at), 'yyyy-MM-dd')
        if (!grouped[dateKey]) {
          grouped[dateKey] = []
        }
        grouped[dateKey].push(post)
      }
    })
    return grouped
  }, [posts])

  // Generate calendar days for the month
  const calendarDays = useMemo(() => {
    const monthStart = startOfMonth(currentDate)
    const monthEnd = endOfMonth(currentDate)
    const calendarStart = startOfWeek(monthStart)
    const calendarEnd = endOfWeek(monthEnd)
    return eachDayOfInterval({ start: calendarStart, end: calendarEnd })
  }, [currentDate])

  // Split days into weeks
  const weeks = useMemo(() => {
    const result: Date[][] = []
    for (let i = 0; i < calendarDays.length; i += 7) {
      result.push(calendarDays.slice(i, i + 7))
    }
    return result
  }, [calendarDays])

  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

  const navigatePrev = () => setCurrentDate(subMonths(currentDate, 1))
  const navigateNext = () => setCurrentDate(addMonths(currentDate, 1))
  const goToToday = () => setCurrentDate(new Date())

  // Count posts without scheduled dates
  const unscheduledPosts = posts.filter(p => !p.scheduled_at)

  return (
    <div className="space-y-4">
      {/* Calendar Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={navigatePrev}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="icon" onClick={navigateNext}>
            <ChevronRight className="h-4 w-4" />
          </Button>
          <h2 className="text-lg font-semibold ml-2">{format(currentDate, 'MMMM yyyy')}</h2>
        </div>
        <Button variant="outline" size="sm" onClick={goToToday}>Today</Button>
      </div>

      {/* Unscheduled posts notice */}
      {unscheduledPosts.length > 0 && (
        <div className="bg-muted/50 border rounded-lg p-3 flex items-center gap-2">
          <Clock className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm text-muted-foreground">
            {unscheduledPosts.length} post{unscheduledPosts.length > 1 ? 's' : ''} without scheduled date
          </span>
        </div>
      )}

      {/* Calendar Grid */}
      <Card>
        <CardContent className="p-0">
          {/* Week day headers */}
          <div className="grid grid-cols-7 border-b">
            {weekDays.map(day => (
              <div key={day} className="p-2 text-center text-sm font-medium text-muted-foreground border-r last:border-r-0">
                {day}
              </div>
            ))}
          </div>

          {/* Calendar weeks */}
          {weeks.map((week, weekIndex) => (
            <div key={weekIndex} className="grid grid-cols-7 min-h-[120px]">
              {week.map((day) => {
                const dateKey = format(day, 'yyyy-MM-dd')
                const dayPosts = postsByDate[dateKey] || []
                const isCurrentMonth = isSameMonth(day, currentDate)
                const isDayToday = isToday(day)

                return (
                  <div
                    key={dateKey}
                    className={`border-b border-r last:border-r-0 p-1 flex flex-col ${
                      !isCurrentMonth ? 'bg-muted/30' : ''
                    } ${isDayToday ? 'bg-primary/5' : ''}`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className={`text-sm font-medium px-1.5 py-0.5 rounded ${
                        isDayToday
                          ? 'bg-primary text-primary-foreground'
                          : !isCurrentMonth
                            ? 'text-muted-foreground'
                            : ''
                      }`}>
                        {format(day, 'd')}
                      </span>
                      {dayPosts.length > 0 && (
                        <Badge variant="secondary" className="text-xs h-5">
                          {dayPosts.length}
                        </Badge>
                      )}
                    </div>
                    <div className="flex-1 overflow-y-auto space-y-1">
                      {dayPosts.slice(0, 3).map(post => {
                        const statusConfig = STATUS_CONFIG[post.status]
                        return (
                          <div
                            key={post.post_id}
                            className="text-xs p-1.5 rounded cursor-pointer truncate bg-primary/10 hover:bg-primary/20 transition-colors"
                            onClick={() => onPostClick(post)}
                            title={post.post_title || post.post_content}
                          >
                            <div className="flex items-center gap-1">
                              <span className="flex-shrink-0">{statusConfig.icon}</span>
                              <span className="truncate">{post.post_title || post.post_content.substring(0, 30)}</span>
                            </div>
                            {post.scheduled_at && (
                              <div className="text-[10px] text-muted-foreground mt-0.5">
                                {format(parseISO(post.scheduled_at), 'h:mm a')}
                              </div>
                            )}
                          </div>
                        )
                      })}
                      {dayPosts.length > 3 && (
                        <div className="text-xs text-muted-foreground text-center">
                          +{dayPosts.length - 3} more
                        </div>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  )
}
